// Surfaces the full details of a failed live-preview request — message,
// HTTP status, error code, and (when the backend attached it) the
// server-side traceback — with a one-click "Copy" button so the author
// can paste the whole thing to an agent to get it fixed.

import { useState } from 'react'
import { Check, Copy, AlertTriangle } from 'lucide-react'
import Modal from '@/components/common/Modal'
import { formatErrorReport } from '../utils'
import type { ParsedApiError } from '../utils'

export interface ErrorReport {
  context: string
  parsed: ParsedApiError
}

interface Props {
  report: ErrorReport | null
  onClose: () => void
}

export default function ErrorDetailsModal({ report, onClose }: Props) {
  const [copied, setCopied] = useState(false)

  if (!report) return null

  const fullText = formatErrorReport(report.context, report.parsed)

  const copy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(fullText)
      } else {
        // Fallback for non-secure contexts where the async clipboard API
        // is unavailable: stage the text in a hidden textarea and use the
        // legacy execCommand copy.
        const ta = document.createElement('textarea')
        ta.value = fullText
        ta.style.position = 'fixed'
        ta.style.opacity = '0'
        document.body.appendChild(ta)
        ta.select()
        document.execCommand('copy')
        document.body.removeChild(ta)
      }
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1800)
    } catch {
      setCopied(false)
    }
  }

  return (
    <Modal
      open={!!report}
      onClose={onClose}
      size="full"
      title="Live preview error"
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
          <button className="btn btn-primary" onClick={copy}>
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? 'Copied' : 'Copy error'}
          </button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 8,
          color: '#ef4444', fontSize: '0.9125rem', fontWeight: 600,
        }}>
          <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>{report.context}</span>
        </div>

        <pre style={{
          margin: 0,
          maxHeight: '60vh',
          overflow: 'auto',
          background: 'var(--color-bg)',
          border: '1px solid var(--color-border)',
          borderRadius: 6,
          padding: 12,
          fontFamily: 'var(--font-mono, monospace)',
          fontSize: '0.8225rem',
          lineHeight: 1.5,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          color: 'var(--color-text)',
        }}>
          {fullText}
        </pre>
      </div>
    </Modal>
  )
}
